import React, { useEffect, useState } from "react";
import firebaseApp from "./Firecontact";
import { Container, Row, Col, Badge, Button, Carousel } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import Header from "./Header";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

// Professional Skeleton Component - Using gap instead of margin
const ProjectShowcaseSkeleton = ({ isEven }) => {
  return (
    <div style={{ padding: "48px 0" }}>
      <Row className="align-items-center g-4 g-md-5">
        {/* Image Skeleton */}
        <Col lg={6} className={isEven ? "order-lg-1" : "order-lg-2"}>
          <div
            className="skeleton-shimmer rounded-3 overflow-hidden"
            style={{ height: "350px" }}
          />
        </Col>

        {/* Content Skeleton */}
        <Col lg={6} className={isEven ? "order-lg-2" : "order-lg-1"}>
          <div className="d-flex flex-column" style={{ gap: "20px" }}>
            {/* Badge Skeleton */}
            <div
              className="skeleton-shimmer rounded-pill"
              style={{ width: "120px", height: "32px" }}
            />

            {/* Title Skeleton */}
            <div
              className="skeleton-shimmer rounded"
              style={{ width: "85%", height: "48px" }}
            />

            {/* Description Skeleton */}
            <div className="d-flex flex-column" style={{ gap: "8px" }}>
              {[100, 100, 100, 75].map((width, i) => (
                <div
                  key={i}
                  className="skeleton-shimmer rounded"
                  style={{ width: `${width}%`, height: "18px" }}
                />
              ))}
            </div>

            {/* Tech Skeleton */}
            <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
              {[80, 100, 70].map((width, i) => (
                <div
                  key={i}
                  className="skeleton-shimmer rounded-pill"
                  style={{ width: `${width}px`, height: "36px" }}
                />
              ))}
            </div>

            {/* Button Skeleton */}
            <div
              className="skeleton-shimmer rounded-pill"
              style={{ width: "200px", height: "52px" }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

const ClientProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const db = firebaseApp.firestore();
        const snapshot = await db
          .collection("clientProjects")
          .orderBy("order", "asc")
          .get();

        const allProjects = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Filter to show only projects for 9rnfc or both websites
        const filteredProjects = allProjects.filter(
          (p) => p.website === "9rnfc" || p.website === "both" || !p.website
        );
        setProjects(filteredProjects);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Projects | 9R NFC</title>
        <meta
          name="description"
          content="Explore our portfolio of web development, mobile apps, and branding projects."
        />
      </Helmet>

      <div className="section-background">
        <Header name="projects" />

        {/* Hero Section - Same as Product page */}
        <section className="py-3 pt-4 section-background">
          <div className="container">
            <div className="d-flex justify-content-between align-items-center align-self-center">
              <h3 className="text-dark fw-600 d-none d-md-block pb-0">
                Our <span className="text-orange">Projects</span>
              </h3>
              <ol className="d-flex justify-content-center align-items-center">
                <li>
                  <Link to={"/Home"} className="text-orange">
                    Home
                  </Link>
                </li>
                <span className="mx-2">/</span>
                <li>Projects</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section style={{ backgroundColor: "#ffffff", padding: "60px 0" }}>
          <Container>
            {loading ? (
              <div className="d-flex flex-column" style={{ gap: "40px" }}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <ProjectShowcaseSkeleton key={i} isEven={i % 2 === 0} />
                ))}
              </div>
            ) : (
              <div className="projects-container">
                {projects.map((project, index) => {
                  const isEven = index % 2 === 0;

                  return (
                    <div
                      key={project.id}
                      className="project-showcase-card position-relative"
                      style={{
                        padding: index === 0 ? "48px 0" : "0",
                        borderBottom:
                          index < projects.length - 1
                            ? "1px solid #d3d3d3ff"
                            : "none",
                      }}
                    >
                      {/* Project Number Watermark */}
                      <span className="project-number d-none d-lg-block">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <Row className="align-items-center g-4 g-md-5">
                        {/* Image Column */}
                        <Col
                          lg={6}
                          className={`${isEven ? "order-lg-1" : "order-lg-2"} mt-0`}
                        >
                          <div className="project-image-wrapper">
                            {project.images && project.images.length > 1 ? (
                              <Carousel
                                indicators={project.images.length > 1}
                                controls={project.images.length > 1}
                                interval={4000}
                              >
                                {project.images.map((img, idx) => (
                                  <Carousel.Item key={idx}>
                                    <img
                                      className="d-block w-100"
                                      src={img}
                                      alt={`${project.title} - ${idx + 1}`}
                                      style={{
                                        width: "100%",
                                        height: "auto",
                                        backgroundColor: "#f8f9fa",
                                      }}
                                    />
                                  </Carousel.Item>
                                ))}
                              </Carousel>
                            ) : (
                              <img
                                src={
                                  project.images && project.images[0]
                                    ? project.images[0]
                                    : ""
                                }
                                alt={project.title}
                                className="w-100"
                                style={{
                                  width: "100%",
                                  height: "auto",
                                  backgroundColor: "#f8f9fa",
                                }}
                              />
                            )}
                          </div>
                        </Col>

                        {/* Content Column */}
                        <Col
                          lg={6}
                          className={isEven ? "order-lg-2" : "order-lg-1"}
                        >
                          <div
                            className="project-content-wrapper position-relative"
                            style={{ zIndex: 1 }}
                          >
                            {/* Category Badge */}
                            <Badge
                              className="px-4 py-2 fw-500 text-uppercase"
                              style={{
                                background:
                                  "linear-gradient(135deg, #3085de 0%, #1e5bb7 100%)",
                                fontSize: "0.75rem",
                                letterSpacing: "1px",
                                borderRadius: "30px",
                                boxShadow: "0 4px 15px rgba(48, 133, 222, 0.3)",
                                width: "fit-content",
                              }}
                            >
                              {project.category}
                            </Badge>

                            {/* Title */}
                            <h2
                              className="fw-bold text-dark"
                              style={{
                                fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                                lineHeight: 1.2,
                                margin: 0,
                              }}
                            >
                              {project.title}
                            </h2>

                            {/* Description */}
                            <p
                              className="text-secondary"
                              style={{
                                fontSize: "1.05rem",
                                // lineHeight: '1.9',
                                maxWidth: "540px",
                                margin: 0,
                              }}
                            >
                              {project.description}
                            </p>

                            {/* Technologies */}
                            {project.technologies && (
                              <div className="tech-stack-container">
                                <h6
                                  className="fw-bold text-dark d-flex align-items-center"
                                  style={{
                                    fontSize: "0.85rem",
                                    letterSpacing: "0.5px",
                                    margin: 0,
                                    gap: "8px",
                                  }}
                                >
                                  <i
                                    className="lni lni-code-alt text-primary"
                                    style={{ fontSize: "1.1rem" }}
                                  ></i>
                                  TECH STACK
                                </h6>
                                {Array.isArray(project.technologies) ? (
                                  <div
                                    className="d-flex flex-wrap"
                                    style={{ gap: "10px" }}
                                  >
                                    {project.technologies.map((tech, idx) => (
                                      <div
                                        key={idx}
                                        className="tech-badge d-flex align-items-center px-3 py-2"
                                        style={{
                                          backgroundColor: "#fff",
                                          borderRadius: "50px",
                                          border: "1px solid #e5e7eb",
                                          boxShadow:
                                            "0 2px 8px rgba(0,0,0,0.04)",
                                          gap: "8px",
                                        }}
                                      >
                                        {tech.logo && (
                                          <img
                                            src={tech.logo}
                                            alt={tech.name}
                                            style={{
                                              width: "22px",
                                              height: "22px",
                                              objectFit: "contain",
                                            }}
                                          />
                                        )}
                                        <span
                                          className="text-dark"
                                          style={{
                                            fontSize: "0.85rem",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {tech.name}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p
                                    className="text-secondary"
                                    style={{ margin: 0 }}
                                  >
                                    {project.technologies}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Testimonial */}
                            {project.testimonial && (
                              <div
                                className="testimonial-box position-relative d-flex flex-column"
                                style={{
                                  backgroundColor: "rgba(255, 193, 7, 0.08)",
                                  borderRadius: "12px",
                                  borderLeft: "4px solid #ffc107",
                                  padding: "20px",
                                  gap: "8px",
                                }}
                              >
                                <i
                                  className="lni lni-quotation position-absolute text-warning"
                                  style={{
                                    fontSize: "2rem",
                                    top: "10px",
                                    left: "15px",
                                    opacity: 0.3,
                                  }}
                                ></i>
                                <p
                                  className="fst-italic text-dark"
                                  style={{
                                    fontSize: "0.95rem",
                                    lineHeight: 1.7,
                                    margin: 0,
                                    paddingLeft: "32px",
                                  }}
                                >
                                  "{project.testimonial}"
                                </p>
                                <small
                                  className="text-muted fw-600"
                                  style={{ paddingLeft: "32px" }}
                                >
                                  — Client Feedback
                                </small>
                              </div>
                            )}

                            {/* CTA Button */}
                            {project.link && (
                              <Button
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-cta-btn btn-main px-5 py-3 fw-600 border-0"
                                style={{
                                  borderRadius: "50px",
                                  fontSize: "0.95rem",
                                  width: "fit-content",
                                }}
                              >
                                {project.buttonText || "View Live Project"}
                                <i
                                  className="lni lni-arrow-right"
                                  style={{ marginLeft: "8px" }}
                                ></i>
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </div>
            )}
          </Container>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ClientProjects;
